import { Theme } from "@mui/material";
import { isNil, sum } from "lodash";

import { Config } from "@/hooks/useConfig";
import { BCPDataWithTotal } from "@/shared/types";
import { Layer } from "@/Utils/hierarchy";

import { LegendOptions } from "./hooks/useColumnChartLegends";

const NEGLIGIBLE_VALUE = 0.01;

const styles = {
  container: "padding: 8px;",
  header: (theme: Theme) => `
    margin-bottom: 12px;
    padding-bottom: 8px;
    margin-right: 48px;
    border-bottom: 1px solid ${theme.palette.divider};
  `,
  headerText: "font-size: 20px; font-weight: 600;",
  sectionHeader: "display: flex; align-items: center; gap: 8px; margin-bottom: 8px;",
  dot: "width: 8px; height: 8px; border-radius: 50%;",
  sectionTitle: "font-size: 16px; font-weight: 600;",
  breakdown: "display: flex; flex-direction: column; gap: 8px;",
  label: "font-weight: 600;",
  italic: "font-style: italic;",
  row: "display: flex; flex-direction: column; gap: 1px; font-size: 14px;",
};

const createHeader = (channel: string, theme: Theme) => `
  <div style="${styles.header(theme)}">
    <span style="${styles.headerText}">${channel}</span>
  </div>
`;

const createSectionHeader = (title: string, color?: string) => `
  <div style="${styles.sectionHeader}">
    ${color ? `<div style="${styles.dot} background-color: ${color};"></div>` : ""}
    <span style="${styles.sectionTitle}">${title}</span>
  </div>
`;

const insufficientDataDisclaimer =
  "Not enough data to show insights for this channel. Try expanding the time period.";

export const renderInsufficientDataTooltip = (channel: string, theme: Theme) => `
  <div style="${styles.container}">
    ${createHeader(channel, theme)}
    <div style='width: 220px; white-space: normal; overflow-wrap: break-word;'>
      <span>${insufficientDataDisclaimer}</span>
    </div>
  </div>
`;

export const renderDataTooltip = (channel: string, theme: Theme, text: string) => `
  <div style="${styles.container}">
    ${createHeader(channel, theme)}
    <div style='width: 220px; white-space: normal; overflow-wrap: break-word;'>
      <span>${text}</span>
    </div>
  </div>
`;

const getValue = (value: number, formatter: (value: number) => string) => {
  if (value === 0 || Math.abs(value) > NEGLIGIBLE_VALUE) return formatter(value);
  if (isNil(value)) return "N/A";
  return `< ${formatter(NEGLIGIBLE_VALUE)}`;
};

const renderGenericBreakdown = (
  breakdown: BCPDataWithTotal,
  formatter: (value: number) => string,
  labels: {
    brand: string;
    category: string;
    product: string;
    total: string;
  }
) => `
  <div style="${styles.breakdown}">
    <div style="${styles.row}">
      <span style="${styles.label}">${labels.brand}</span>
      <span>${getValue(breakdown.brand, formatter)}</span>
    </div>
    <div style="${styles.row}">
      <span style="${styles.label}">${labels.category}</span>
      <span>${getValue(breakdown.category, formatter)}</span>
    </div>
    <div style="${styles.row}">
      <span style="${styles.label}">${labels.product}</span>
      <span>${getValue(breakdown.product, formatter)}</span>
    </div>
    <div style="${styles.row}">
      <span style="${styles.label}">${labels.total}</span>
      <span>${getValue(breakdown.total, formatter)}</span>
    </div>
  </div>
`;

const renderBreakdown = (
  breakdown: BCPDataWithTotal,
  config: Config,
  type: string,
  formatter: (value: number) => string
) =>
  renderGenericBreakdown(breakdown, formatter, {
    brand: config.getLayerLabel(Layer.Brand),
    category: config.getLayerLabel(Layer.Category),
    product: config.getLayerLabel(Layer.Product),
    total: `Total ${type}`,
  });

const formatBreakdownTooltip = (
  type: string,
  breakdown: BCPDataWithTotal,
  config: Config,
  formatter: (value: number) => string,
  color?: string
) => `
  <div>
    ${createSectionHeader(`Marketing ${type}`, color)}
    <div style="${color ? "margin-left: 16px;" : ""}">
      ${renderBreakdown(breakdown, config, type, formatter)}
    </div>
  </div>
`;

export const formatSpendAndRevenueTooltip = (
  channel: string,
  metrics: {
    spend: BCPDataWithTotal;
    revenue: BCPDataWithTotal;
    yield: BCPDataWithTotal;
  },
  legendOptions: Pick<LegendOptions, "showMarketingSpend" | "showMarketingRevenue">,
  config: Config,
  spendFormatter: (value: number) => string,
  revenueFormatter: (value: number) => string,
  yieldFormatter: (value: number) => string,
  theme: Theme,
  isPercentage: boolean
) => {
  const { revenueLabel } = config;
  return `
    <div style="${styles.container}">
      ${createHeader(channel, theme)}
      <div style="display: flex; flex-direction: row; gap: 16px;">
        ${legendOptions.showMarketingSpend ? formatBreakdownTooltip("Spend", metrics.spend, config, spendFormatter, theme.palette.channelEffectivenessChart?.marketingSpend) : ""}
        ${legendOptions.showMarketingRevenue ? formatBreakdownTooltip(revenueLabel, metrics.revenue, config, revenueFormatter, theme.palette.channelEffectivenessChart?.marketingRevenue) : ""}
        ${!isPercentage ? formatBreakdownTooltip("Yield", metrics.yield, config, yieldFormatter) : ""}
      </div>
    </div>
  `;
};

export const formatROITooltip = (
  channel: string,
  breakdown: BCPDataWithTotal,
  config: Config,
  formatter: (value: number) => string,
  theme: Theme
) => `
  <div style="${styles.container}">
    ${createHeader(channel, theme)}
    <div>
      ${createSectionHeader("Marketing ROI", theme.palette.channelEffectivenessChart?.roi)}
      ${renderBreakdown(breakdown, config, "ROI", formatter)}
    </div>
  </div>
`;

export const formatSoeTooltip = (
  channel: string,
  breakdown: BCPDataWithTotal,
  formatter: (value: number) => string,
  theme: Theme,
  title: string,
  disclaimerText: string
) => `
  <div style="${styles.container}">
    ${createHeader(channel, theme)}
    <div>
      ${createSectionHeader(title, theme.palette.channelEffectivenessChart?.shareOfEffect)}
      <div style='margin: 0 0 8px 16px;'>${formatter(breakdown.total)}</div>
      <div style="font-style: italic; margin-top: 8px; width: 220px; white-space: normal; overflow-wrap: break-word;">
        ${disclaimerText}
      </div>
    </div>
  </div>
`;

const getCombinedTotalsBreakdown = (breakdown: BCPDataWithTotal) => ({
  ...breakdown,
  brand: sum([breakdown.brand, breakdown.upperFunnelMarketingContribution]),
  category: sum([breakdown.category, breakdown.midFunnelMarketingContribution]),
});

export const formatMarketingReturnAmountTooltip = (
  channel: string,
  breakdown: BCPDataWithTotal,
  config: Config,
  formatter: (value: number) => string,
  theme: Theme
) => {
  const combinedTotalsBreakdown = getCombinedTotalsBreakdown(breakdown);
  const revenueLabel = config.showReturnInsteadOfRevenue ? "Return" : "Revenue";
  return `
  <div style="${styles.container}">
    ${createHeader(channel, theme)}
    <div>
      ${createSectionHeader("Marketing Return", theme.palette.channelEffectivenessChart?.marketingReturn)}
      ${renderGenericBreakdown(combinedTotalsBreakdown, formatter, {
        brand: `${config.getLayerLabel(Layer.Brand)} Share of Effect`,
        category: `${config.getLayerLabel(Layer.Category)} Share of Effect`,
        product: `${config.getLayerLabel(Layer.Product)} ${revenueLabel}`,
        total: `Total ${revenueLabel}`,
      })}
    </div>
  </div>
`;
};

export const formatMarketingReturnPercentageTooltip = (
  channel: string,
  breakdown: BCPDataWithTotal,
  config: Config,
  formatter: (value: number) => string,
  theme: Theme
) => `
  <div style="${styles.container}">
    ${createHeader(channel, theme)}
    <div>
      ${createSectionHeader("Percentage of total return", theme.palette.channelEffectivenessChart?.marketingReturn)}
      ${renderBreakdown(getCombinedTotalsBreakdown(breakdown), config, "Percentage", formatter)}
    </div>
  </div>
`;
